/**
 * Room addressing and invite links.
 *
 * The brief was: friends should be able to join, strangers should not be able
 * to find the game. The scheme that achieves that without accounts is a short,
 * human-readable room id for saying out loud, plus a long random key that only
 * travels in the invite link. The id alone gets you nowhere — the server will
 * not admit a socket that cannot present the key, and there is no endpoint
 * that lists or searches rooms at all.
 *
 * The key lives in the URL fragment, which browsers never put in a Referer
 * header and servers never see in their access logs.
 */

/** Crockford-style alphabet: no I, L, O, U — nothing to mishear or mistype. */
const ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

function randomFrom(alphabet: string, length: number): string {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  let out = '';
  // Rejection-free because 256 is not a multiple of 32; with a 32-character
  // alphabet the modulo is exactly uniform.
  for (const b of bytes) out += alphabet[b % alphabet.length];
  return out;
}

/** ~30 bits. Short enough to read out, and useless without the key. */
export const newRoomId = (): string => randomFrom(ALPHABET, 6);

/** 128 bits of entropy. This is the thing that actually gates entry. */
export const newRoomKey = (): string => randomFrom(ALPHABET, 26);

export interface RoomLink { roomId: string; key: string; }

/** Parse `#/r/<id>/<key>` out of the current URL. */
export function readLink(hash = location.hash): RoomLink | null {
  const m = /^#\/r\/([0-9A-Za-z]{4,12})\/([0-9A-Za-z]{16,64})$/.exec(hash);
  return m ? { roomId: (m[1] as string).toUpperCase(), key: m[2] as string } : null;
}

export const linkFor = (r: RoomLink): string =>
  `${location.origin}${location.pathname}#/r/${r.roomId}/${r.key}`;

export function writeLink(r: RoomLink | null): void {
  const next = r ? `#/r/${r.roomId}/${r.key}` : '#';
  if (location.hash !== next) history.replaceState(null, '', next);
}

/** WebSocket endpoint for a room. */
/**
 * The address to open a table's socket on.
 *
 * `secret` is this device's private identity. The server turns it into the
 * public player id everyone else sees, by a one-way hash, so it is the only
 * thing that can ever reclaim this player's seat — and it must never appear in
 * an invite link or in anything the game shows. It is not the invite key.
 */
export function socketUrl(r: RoomLink, secret: string, name: string): string {
  const base = import.meta.env['VITE_SERVER_URL'] as string | undefined;
  const origin = base && base.length > 0
    ? base.replace(/^http/, 'ws')
    : `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}`;
  const q = new URLSearchParams({ k: r.key, s: secret, n: name });
  return `${origin}/room/${r.roomId}/ws?${q.toString()}`;
}

/** Copy to clipboard, falling back to a selection when the API is blocked. */
export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.cssText = 'position:fixed;opacity:0';
      document.body.append(ta);
      ta.select();
      const ok = document.execCommand('copy');
      ta.remove();
      return ok;
    } catch {
      return false;
    }
  }
}
